import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@longy/web-components'
import { Link, useLocation } from '@tanstack/react-router'
import { ChevronDown } from 'lucide-react'

import { HeaderNavEntry, isHeaderNavEntryActive } from '../../utils/headerNav'
import { HeaderAccountMenu } from '../auth/HeaderAccountMenu'
import { LanguageSwitcher } from '../LanguageSwitcher'
import { BookingCtaButton } from './BookingCtaButton'

const linkClassName = (isActive: boolean) =>
    `text-sm font-medium transition-colors hover:text-primary ${
        isActive ? 'text-primary' : 'text-brand-gray'
    }`

type HeaderMobileMenuProps = {
    entries: HeaderNavEntry[]
    onNavigate: () => void
    onBookingCtaClick: () => void
}

export const HeaderMobileMenu = ({
    entries,
    onNavigate,
    onBookingCtaClick,
}: HeaderMobileMenuProps) => {
    const location = useLocation()

    return (
        <div className="border-t border-slate-100 bg-white px-6 py-4 lg:hidden">
            <BookingCtaButton className="mb-4 w-full" onClick={onBookingCtaClick} />

            <nav className="flex flex-col gap-4">
                {entries.map((entry) => {
                    if (entry.kind === 'link') {
                        return (
                            <Link
                                key={entry.href}
                                to={entry.href}
                                className={linkClassName(entry.href === location.pathname)}
                                onClick={onNavigate}
                            >
                                {entry.label}
                            </Link>
                        )
                    }

                    // Collapsed rather than flattened into the list: on a phone the cms pages alone
                    // would push the product navigation off the screen.
                    return (
                        <Collapsible key={entry.id}>
                            <CollapsibleTrigger
                                className={`flex w-full items-center justify-between [&[data-state=open]>svg]:rotate-180 ${linkClassName(
                                    isHeaderNavEntryActive(entry, location.pathname),
                                )}`}
                            >
                                {entry.label}
                                <ChevronDown className="size-4 transition-transform duration-200" />
                            </CollapsibleTrigger>
                            <CollapsibleContent className="mt-3 flex flex-col gap-3 border-l border-slate-100 pl-4">
                                {entry.items.map((item) => (
                                    <Link
                                        key={item.href}
                                        to={item.href}
                                        className={linkClassName(item.href === location.pathname)}
                                        onClick={onNavigate}
                                    >
                                        {item.label}
                                    </Link>
                                ))}
                            </CollapsibleContent>
                        </Collapsible>
                    )
                })}
            </nav>

            <div className="mt-4 flex items-center gap-3 border-t border-slate-100 pt-4">
                <HeaderAccountMenu />
                <LanguageSwitcher />
            </div>
        </div>
    )
}
